/**
 * AuthContext — the single auth abstraction behind which both login paths live:
 *
 *   1. Password login (FULLY implemented now): client.login() → token in body →
 *      tokenStore → decode `system_roles` → identity. Self-contained + testable.
 *
 *   2. SSO (Google/Microsoft): startSso() builds the org-scoped authorize_url and
 *      redirects the browser. The callback delivers tokens as HttpOnly cookies
 *      (JS can't read them), so identity is resolved via client.getMe(). This
 *      path is PLUGGABLE: `resolveIdentity()` already prefers getMe().system_roles
 *      and only needs the oxbo-auth /me change (adding the system_roles array) +
 *      the admin-org provider config to be production-wired. See ADMIN-PR0 notes.
 *
 * Gating is ALWAYS on the full `system_roles` list (never the lossy scalar).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { OxboAuthClient } from "@oxbo/auth-client";
import { authClient as defaultClient } from "./authClient";
import { tokenStore } from "./tokenStore";
import {
  decodeJwt,
  hasSuperAdmin,
  isExpired,
  systemRolesFromClaims,
} from "./jwt";

export type SsoProvider = "google" | "microsoft";

export interface Identity {
  userId: string;
  email: string;
  orgId: string;
  systemRoles: string[];
  isSuperAdmin: boolean;
  /** Where the identity came from — useful for diagnostics. */
  source: "token" | "me";
}

export interface AuthContextValue {
  identity: Identity | null;
  /** True while the initial session bootstrap is in flight. */
  loading: boolean;
  loginWithPassword: (email: string, password: string) => Promise<Identity>;
  startSso: (provider: SsoProvider, organizationId: string) => Promise<void>;
  /** Resolve identity after an SSO cookie callback (cookie-auth getMe path). */
  completeSsoCallback: () => Promise<Identity | null>;
  logout: () => Promise<void>;
  /** Re-read whatever session currently exists (token or cookie). */
  refreshIdentity: () => Promise<Identity | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SSO_STATE_KEY = "oxbo_admin_sso_state";

function identityFromClaimsToken(token: string): Identity | null {
  const claims = decodeJwt(token);
  if (!claims || isExpired(claims)) return null;
  const systemRoles = systemRolesFromClaims(claims);
  return {
    userId: String(claims.user_id ?? claims.sub ?? ""),
    email: String(claims.email ?? ""),
    orgId: String(claims.org_id ?? ""),
    systemRoles,
    isSuperAdmin: hasSuperAdmin(systemRoles),
    source: "token",
  };
}

export interface AuthProviderProps {
  children: ReactNode;
  /** Inject a client in tests; defaults to the shared instance. */
  client?: OxboAuthClient;
}

export function AuthProvider({ children, client = defaultClient }: AuthProviderProps): ReactNode {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [loading, setLoading] = useState(true);
  const clientRef = useRef(client);
  clientRef.current = client;

  /**
   * Resolve the current identity from whatever session exists:
   *   - a stored access token (password flow) → decode + gate on system_roles;
   *   - otherwise try cookie-auth getMe() (SSO flow). We prefer the full
   *     `system_roles` array and fall back to the lossy scalar only if that's
   *     all the server returns (pending the oxbo-auth /me enhancement).
   */
  const resolveIdentity = useCallback(async (): Promise<Identity | null> => {
    const token = tokenStore.getAccessToken();
    if (token) {
      const id = identityFromClaimsToken(token);
      if (id) return id;
      // Stale/expired token — drop it and fall through to the cookie path.
      tokenStore.clear();
    }

    try {
      const me = (await clientRef.current.getMe()) as {
        user_id: string;
        org_id: string;
        email?: string;
        system_role?: string;
        system_roles?: string[];
      };
      const systemRoles = Array.isArray(me.system_roles)
        ? me.system_roles
        : me.system_role
          ? [me.system_role]
          : [];
      return {
        userId: me.user_id,
        email: me.email ?? "",
        orgId: me.org_id,
        systemRoles,
        isSuperAdmin: hasSuperAdmin(systemRoles),
        source: "me",
      };
    } catch {
      return null;
    }
  }, []);

  const refreshIdentity = useCallback(async (): Promise<Identity | null> => {
    const id = await resolveIdentity();
    setIdentity(id);
    return id;
  }, [resolveIdentity]);

  // Initial bootstrap.
  useEffect(() => {
    let active = true;
    (async () => {
      const id = await resolveIdentity();
      if (active) {
        setIdentity(id);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [resolveIdentity]);

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<Identity> => {
      const res = await clientRef.current.login({ email, password });
      tokenStore.setTokens(res.access_token, res.refresh_token);
      const id = identityFromClaimsToken(res.access_token);
      if (!id) {
        tokenStore.clear();
        throw new Error("Login succeeded but the access token could not be decoded.");
      }
      setIdentity(id);
      return id;
    },
    [],
  );

  const startSso = useCallback(
    async (provider: SsoProvider, organizationId: string): Promise<void> => {
      if (!organizationId) {
        throw new Error(
          "SSO requires an organization_id. The admin bootstrap org is not yet " +
            "configured (VITE_ADMIN_ORG_ID) — pending the admin-org provider setup.",
        );
      }
      const fn =
        provider === "google"
          ? clientRef.current.googleLogin.bind(clientRef.current)
          : clientRef.current.microsoftLogin.bind(clientRef.current);
      const { authorize_url, state } = await fn({ organization_id: organizationId });
      // Persist state for CSRF cross-check on return.
      try {
        sessionStorage.setItem(SSO_STATE_KEY, state);
      } catch {
        /* ignore */
      }
      window.location.assign(authorize_url);
    },
    [],
  );

  const completeSsoCallback = useCallback(async (): Promise<Identity | null> => {
    // The auth service sets HttpOnly cookies and 302s here; nothing to read
    // from the URL. Identity comes from cookie-auth getMe().
    return refreshIdentity();
  }, [refreshIdentity]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await clientRef.current.logout();
    } catch {
      // best-effort — clear local state regardless
    }
    tokenStore.clear();
    try {
      sessionStorage.removeItem(SSO_STATE_KEY);
    } catch {
      /* ignore */
    }
    setIdentity(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      identity,
      loading,
      loginWithPassword,
      startSso,
      completeSsoCallback,
      logout,
      refreshIdentity,
    }),
    [identity, loading, loginWithPassword, startSso, completeSsoCallback, logout, refreshIdentity],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>.");
  }
  return ctx;
}
